/* eslint-disable max-lines */
import {
    useContext, useEffect, useMemo, useState
} from 'react';
import {
 v4 as uuidv4
} from 'uuid';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    Context
} from 'shared/context/GlobalState';
import {
    Terminal
} from 'lucide-react';
import {
    OpCodeBuilderContext
} from '../context/OpCodeBuilderContext';
import {
    Input
} from 'shared/components/ui/input';
import {
    Checkbox
} from 'shared/components/ui/checkbox';
import {
    Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
    RedisJSONOpcode
} from './types';
import {
    DisplayCodeBlock
} from './showExecuteResponse';
import {
    useWebSocket
} from '../hooks/useSocket';

type InputField = {
    key: string;
    value: string;
};

// A-0008.05: revamped wire shape — `inputs` (was `context`), nested `streaming`,
// optional `client_request_id`. `stream` flag dropped (always streams).
type ExecuteBody = {
    opcode_id: string;
    org_id: string;
    sub_org_id: string;
    inputs: Record<string, unknown>;
    streaming: {
        emit_tokens: boolean;
        buffer_sentences: boolean;
    };
    debug: boolean;
    memory_log_id?: string;
    client_request_id?: string;
};

// A-0008.03: single opcode endpoint (no v2/v3 split).
const EXECUTE_URL = 'api/ws/projecto/config/api/opcode/execute';

export default function JsonOpcodeExecute({
    opCode
}: {
    opCode: RedisJSONOpcode | null;
}) {
    const {
        orgId, subOrgId
    } = useContext(OpCodeBuilderContext) as { orgId: string; subOrgId: string };
    const {
        user,
        addNewNotifcation
    } = useContext(Context);

    // Generate unique session ID
    const sessionId = useMemo(() => uuidv4(), []);

    const {
        sendMessage,
        messages,
        isExecuting,
        errorResult,
        taskId // A-0008.05
    } = useWebSocket(`${user._id}-${orgId}-${sessionId}`, EXECUTE_URL);

    const [inputFields, setInputFields] = useState<InputField[]>([]);
    const [memoryLogId, setMemoryLogId] = useState<string>('');
    const [emitTokenEvents, setEmitTokenEvents] = useState<boolean>(false);
    const [streamBufferSentences, setStreamBufferSentences] = useState<boolean>(false);
    const [debug, setDebug] = useState<boolean>(true);

    // Initialize input fields from opcode inputs array
    // A-0008.03: V2+ inputs may be TypedInput[] or legacy string[]; coerce to {key,value} fields either way.
    useEffect(() => {
        if (opCode?.inputs && Array.isArray(opCode.inputs)) {
            const fields: InputField[] = (opCode.inputs as Array<string | { name?: string }>).map((entry) => ({
                key: typeof entry === 'string' ? entry : (entry?.name || ''),
                value: '',
            }));
            setInputFields(fields);
        } else {
            setInputFields([]);
        }
    }, [opCode]);

    // Compute request body dynamically based on current state
    const requestBody = useMemo<ExecuteBody | null>(() => {
        if (!opCode) {
            return null;
        }

        const inputs = inputFields.reduce<Record<string, unknown>>((acc, field) => {
            if (field.value.trim() !== '') {
                try {
                    acc[field.key] = JSON.parse(field.value);
                } catch {
                    acc[field.key] = field.value;
                }
            }
            return acc;
        }, {});

        const baseBody: ExecuteBody = {
            opcode_id: opCode.opcode_id,
            org_id: orgId,
            sub_org_id: subOrgId,
            inputs,
            streaming: {
                emit_tokens: emitTokenEvents,
                buffer_sentences: streamBufferSentences
            },
            debug,
            client_request_id: uuidv4()
        };

        if (memoryLogId.trim() !== '') {
            return {
                ...baseBody,
                memory_log_id: memoryLogId.trim()
            };
        }

        return baseBody;
    }, [opCode, orgId, subOrgId, inputFields, memoryLogId, emitTokenEvents, streamBufferSentences, debug]);

    const handleInputChange = (index: number, value: string) => {
        const newFields = [...inputFields];
        newFields[index].value = value;
        setInputFields(newFields);
    };

    const handleExecute = async () => {
        if (!opCode || !requestBody) {
            addNewNotifcation('Please select a JSON opcode.', 'warning');
            return;
        }

        try {
            sendMessage(requestBody);
        } catch (e) {
            console.error('Error executing JSON opcode:', e);
            addNewNotifcation('Error executing JSON opcode. Please try again.', 'danger');
        }
    };

    if (!opCode) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                No JSON opcode selected
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Left Column - Input Fields */}
            <div className="space-y-6">
                <div className="space-y-4">
                    <Label className="text-lg font-semibold">Input Fields</Label>
                    {inputFields.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">
                            This opcode has no input fields defined.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {inputFields.map((field, index) => (
                                <div key={field.key} className="space-y-2">
                                    <Label htmlFor={`input-${index}`}>{field.key}</Label>
                                    <Input
                                        id={`input-${index}`}
                                        placeholder={`Enter value for ${field.key}`}
                                        value={field.value}
                                        onChange={(e) => handleInputChange(index, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Memory Log ID */}
                <div className="space-y-2">
                    <Label htmlFor="memory-log-id">Memory Log ID (Optional)</Label>
                    <Input
                        id="memory-log-id"
                        placeholder="Enter memory log ID (optional)"
                        value={memoryLogId}
                        onChange={(e) => setMemoryLogId(e.target.value)}
                    />
                </div>

                {/* Boolean Settings */}
                <div className="space-y-4">
                    <Label className="text-lg font-semibold">Execution Settings</Label>
                    <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="emit-token-events"
                                checked={emitTokenEvents}
                                onCheckedChange={(checked) => setEmitTokenEvents(checked === true)}
                            />
                            <Label htmlFor="emit-token-events" className="cursor-pointer">
                                Emit Token Events
                            </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="stream-buffer-sentences"
                                checked={streamBufferSentences}
                                onCheckedChange={(checked) => setStreamBufferSentences(checked === true)}
                            />
                            <Label htmlFor="stream-buffer-sentences" className="cursor-pointer">
                                Stream Buffer Sentences
                            </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="debug"
                                checked={debug}
                                onCheckedChange={(checked) => setDebug(checked === true)}
                            />
                            <Label htmlFor="debug" className="cursor-pointer">
                                Debug
                            </Label>
                        </div>
                    </div>
                </div>

                {/* Request Body Preview */}
                <div className="space-y-2">
                    <Label className="text-lg font-semibold">Request Body</Label>
                    <Card>
                        <CardContent className="pt-6">
                            <DisplayCodeBlock data={requestBody ?? {}} />
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Right Column - Response */}
            <div className="flex flex-col space-y-4">
                <Button
                    onClick={handleExecute}
                    className="w-full my-4"
                    disabled={isExecuting || !opCode?.opcode_id}
                >
                    {isExecuting ? 'Executing opcode...' : 'Execute opcode'}
                </Button>
                {/* A-0008.05: server-issued task_id from Mind's `begin` event, surfaced via WS bridge. */}
                {taskId && (
                    <div className="text-xs font-mono text-muted-foreground -mt-2 mb-2 text-center">
                        task: {taskId.slice(0, 8)}…
                    </div>
                )}
                <div className="flex flex-col gap-2 items-center w-full">
                    <div className='flex gap-2 items-center'>
                        <Terminal className="w-5 h-5 text-primary" />
                        <Label className="text-xl">Execution Results</Label>
                    </div>
                    {messages && (
                        <div className="space-y-4 w-full">
                            {messages.map((msg, idx) => (
                                <DisplayCodeBlock key={idx} data={msg} />
                            ))}
                        </div>
                    )}
                    {errorResult && (
                        <Card className="border-destructive">
                            <CardHeader>
                                <CardTitle className="text-destructive">Error</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <DisplayCodeBlock data={errorResult} />
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
